import { Injectable } from '@angular/core';

import { BrowserType, DeviceDetector, PlatformType } from './device-detector.service';
import { WindowService } from './window.service';

@Injectable({
    providedIn: 'root'
})
export class UnloadEventService {

    private readonly handlePageHide: boolean;

    constructor(
        private readonly windowService: WindowService,
        deviceDetector: DeviceDetector
    ) {
        // Safari on iOS does not support unload event
        const deviceInfo = deviceDetector.getDeviceInfo();
        const platformType = deviceInfo.platform.platformType;
        this.handlePageHide =
            (platformType === PlatformType.iPhone || platformType === PlatformType.iPad) &&
            deviceInfo.browser.browserType === BrowserType.Safari;
    }

    // Events added later will be executed first (Stack). To attempt executing later pass lateExecution = true.
    // Note we only guarantee that all 'lateExecution' actions will execute after all non-'lateExecution' actions
    public addUnloadEvent(eventAction: () => void, lateExecution: boolean = false): void {
        // only call it once across any events
        let called = false;

        const nativeWindow = this.windowService.nativeWindow;

        const executeNewAndOld = (ev: Event, oldAction: any) => {
            let returnValue: any = undefined;

            if (lateExecution && oldAction) {
                returnValue = oldAction(ev);
            }

            if (!called) {
                called = true;
                eventAction();
            }

            if (!lateExecution && oldAction) {
                returnValue = oldAction(ev);
            }

            // IMPORTANT: Return 'undefined' in case oldOnbeforeunload is null.
            // Returning null in IE11 triggers showing "Do you want to leave this page" dialog
            // with 'null' displayed as message text.
            return returnValue;
        };

        // most browsers support onbeforeunload, use that first
        const oldOnbeforeunload = nativeWindow.onbeforeunload;
        nativeWindow.onbeforeunload = ev => executeNewAndOld(ev, oldOnbeforeunload && oldOnbeforeunload.bind(nativeWindow));

        // Some browsers support pagehide
        if (this.handlePageHide) {
            const oldPageHide = nativeWindow.onpagehide;
            nativeWindow.onpagehide = ev => executeNewAndOld(ev, oldPageHide && oldPageHide.bind(nativeWindow));
        }
    }
}
